import React, { Fragment, useReducer, useState, useEffect } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import { LayoutContext, layoutState, layoutReducer } from "./components/shop";
import { Toaster } from 'react-hot-toast';

// IMPORT HÀM KIỂM TRA QUYỀN TỪ API
import { isAuthenticate, isAdmin } from "./components/auth/fetchApi";

import Sidebar from "./components/shop/Sidebar";
import Navbar from "./components/partials/Navbar";
import LoginSignup from "./components/auth/LoginSignup";
import Dashboard from "./components/shop/Dashboard";
import Home from "./components/shop/Home";
import 'bootstrap/dist/css/bootstrap.min.css';

const globalStyles = `
  body { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
  .hover-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
  .hover-card:hover { transform: translateY(-4px); box-shadow: 0 12px 30px rgba(0,0,0,0.15) !important; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #E9E4DB; border-radius: 10px; }
  ::-webkit-scrollbar-thumb:hover { background: #C5A075; }
`;

// ==========================================
// 1. LAYOUT CHO TRANG CHỦ (HOME)
// ==========================================
const HomeLayout = ({ children }) => {
  const { colors } = React.useContext(LayoutContext);

  // LOGIC CHÌA KHÓA: Kiểm tra xem người đang xem có phải Admin không?
  const _isLoggedInAdmin = isAuthenticate() && isAdmin();

  return (
    <div className="d-flex flex-column vh-100 overflow-hidden" style={{ backgroundColor: colors.bgApp, color: colors.textMain }}>
      <style>{globalStyles}</style>

      {/* NẾU LÀ ADMIN ĐANG ĐĂNG NHẬP -> HIỆN NAVBAR. 
          NẾU LÀ KHÁCH BÌNH THƯỜNG -> ẨN NAVBAR HOÀN TOÀN */}
      {_isLoggedInAdmin && <Navbar isAdminView={false} />}

      <div className="flex-grow-1 overflow-auto home-content">
        {children}
      </div>
    </div>
  );
};

// ==========================================
// 2. LAYOUT CHO ADMIN (DASHBOARD)
// ==========================================
const AdminLayout = ({ children }) => {
  const { colors } = React.useContext(LayoutContext);
  return (
    <div className="d-flex vh-100 overflow-hidden" style={{ backgroundColor: colors.bgApp, color: colors.textMain, transition: "all 0.3s ease" }}>
      <style>{globalStyles}</style>
      <Sidebar />
      <div className="flex-grow-1 d-flex flex-column overflow-hidden" style={{ transition: "all 0.3s ease" }}>
        <Navbar isAdminView={true} />
        <div className="flex-grow-1 overflow-auto p-4 p-md-5 admin-content" style={{ backgroundColor: colors.bgApp }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// ROUTE BẢO VỆ CHO ADMIN
// ==========================================
const AdminProtectedRoute = ({ component: Component, ...rest }) => (
  <Route {...rest} render={(props) =>
    isAuthenticate() && isAdmin() ? (
      <AdminLayout>
        <Component {...props} />
      </AdminLayout>
    ) : (
      <Redirect to={{ pathname: "/login", state: { from: props.location } }} />
    )
  } />
);

// ==========================================
// ROUTE BẢO VỆ LOGIN (Đã Login là văng vô Dashboard ngay)
// ==========================================
const LoginRoute = ({ component: Component, ...rest }) => (
  <Route {...rest} render={(props) =>
    isAuthenticate() && isAdmin() ? (
      // Nếu đã Login bằng quyền Admin -> Đá thẳng vào Dashboard
      <Redirect to="/admin/dashboard" />
    ) : (
      // Nếu chưa Login -> Cho phép đứng ở trang đăng nhập
      <Component {...props} />
    )
  } />
);

function App() {
  const [data, dispatch] = useReducer(layoutReducer, layoutState);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  useEffect(() => {
    const savedMode = localStorage.getItem("darkMode");
    if (savedMode !== null) setIsDarkMode(savedMode === "true");
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    localStorage.setItem("darkMode", !isDarkMode);
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const colors = {
    bgApp: isDarkMode ? '#141423' : '#FCFBF7',
    bgSidebar: isDarkMode ? '#141423' : '#FFFFFF',
    bgCard: isDarkMode ? '#202030' : '#FFFFFF',
    textMain: isDarkMode ? '#FFFFFF' : '#3C2A1A',
    textMuted: isDarkMode ? '#8c8c9a' : '#8B7D6B',
    borderColor: isDarkMode ? '#2b2b3c' : '#E9E4DB',
    activeMenu: '#C5A075',
  };

  return (
    <Fragment>
      <LayoutContext.Provider value={{ data, dispatch, isDarkMode, toggleDarkMode, isSidebarOpen, toggleSidebar, colors }}>
        <Router>
          <Toaster position="top-center" />
          <Switch>

            {/* 1. TRANG CHỦ (Chỉ Khách / Admin ra xem thử) */}
            <Route exact path="/">
              <HomeLayout>
                <Home />
              </HomeLayout>
            </Route>

            {/* 2. TRANG ĐĂNG NHẬP (Ẩn danh, tự gõ /login mới ra) */}
            {/* Sử dụng LoginRoute để chặn Admin quay lại trang Login */}
            <LoginRoute exact path="/login" component={LoginSignup} />

            {/* 3. TRANG DASHBOARD ADMIN (Bắt buộc phải có thẻ Admin) */}
            <AdminProtectedRoute exact path="/admin/dashboard" component={Dashboard} />

            {/* 4. TRANG LỖI 404 */}
            <Route render={() => <div className="text-center mt-5 fs-4 fw-bold">404 - Trang không tồn tại</div>} />
          </Switch>
        </Router>
      </LayoutContext.Provider>
    </Fragment>
  );
}

export default App;